import { defineComponent, ref, watch, computed, h } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { container, loader, loadingButton, pageTitle } from '@/ui/components';
import { useTask } from '@/ui/concerns';
import { get, intoEditable, update } from '@/services/api/resources/entityItem';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filter as fetchAllEntityTypes } from '@/services/api/resources/entityType';
import { filterListToOptions, IdProps, sequence, goBack } from '@/ui/helpers/uiHelper';
import { toasted } from '@/ui/helpers';
import { versioned } from '@/services/api';
import { hasFeature, hasPermission, Permissions, user } from '@/ui/auth/auth';
import { SelectOptions } from '@/ui/types';
import { useEntityItemRecord } from './useEntityItemRecord';
import { useEntityItemForm } from './useEntityItemForm';
import { useCreatableUserModal } from '@/ui/pages/useCreatableUserModal';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { useEntityTypePropertiesLoader } from './useEntityTypePropertiesLoader';
import { Icon } from '@/ui/components/icon/Icon';

export const EntityItemEditPage = defineComponent<IdProps>({
  name: 'EntityItemEditPage',
  props: ['id'],
  setup(props) {
    if (!hasFeature('entityItems')) {
      return renderResourceTeaserPage(t('common.entity-items'));
    }

    if (!hasPermission(Permissions.MANAGE_ENTITY_ITEMS) && !hasPermission(Permissions.MANAGE_ENTITY_PROPERTIES)) {
      return renderForbiddenPage;
    }

    let recordVersion = '';
    const userOptions = ref<SelectOptions>([]);
    const entityTypeOptions = ref<SelectOptions>([]);

    const record = useEntityItemRecord();

    const getRecord = useTask(
      () => sequence(get(props.id), getAllUsers(), fetchAllEntityTypes()),
      ([{ data, version }, users, entityTypes]) => {
        userOptions.value = filterListToOptions(users);
        entityTypeOptions.value = filterListToOptions(entityTypes);
        record.value = intoEditable(data);
        recordVersion = version;
      },
    );
    watch(ref(props.id), getRecord.run);

    const sendRecord = useTask(
      () => update(props.id, versioned(record.value, recordVersion)),
      () => {
        toasted.success(t('entity-item.updated'));
        goBack();
      },
    );

    const entityTypeId = computed(() => record.value.entityTypeId);
    const { entityTypeProperties, loadEntityTypeProperties } = useEntityTypePropertiesLoader(entityTypeId);

    watch(() => entityTypeProperties.value, newProperties => {
      if (!record.value.properties) {
        return;
      }

      const validPropertyIds = new Set(newProperties.map(p => p.id));
      record.value.properties = record.value.properties.filter(pv => validPropertyIds.has(pv.entityTypePropertyId));
    });

    const { formFields, handleSubmit } = useEntityItemForm(
      record,
      sendRecord,
      loadEntityTypeProperties,
      user.value,
    );

    useCreatableUserModal(userOptions);

    return function render() {
      return container(
        [
          h('div', { class: 'level level--page-header' }, [
            h('div', { class: 'level-left' }, [
              h(Icon, {
                props: { icon: 'arrow-left' },
                class: 'entity-item-page__back-icon',
                nativeOn: { click: goBack },
              }),
              pageTitle(t('entity-item.edit')),
            ]),
          ]),
          loader('form', getRecord, [
            ...formFields(userOptions.value, entityTypeOptions.value, entityTypeProperties.value),
            loadingButton(
              t('entity-item.update'),
              handleSubmit,
              sendRecord.state.value.pending,
            ),
          ]),
        ],
        'narrow',
        'entity-item-page',
      );
    };
  },
});
